import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/adminlayout';
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/api';
import { PRODUCTION_URL } from '../productionLink/productionLink';

const PosDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [dashboardData, setDashboardData] = useState({
    todaySales: {},
    topItems: [],
    recentOrders: [],
    categoryRevenue: []
  });

  const colors = {
    primary: '#2D5016',
    accent: '#6ba83e',
    lightGray: '#f8faf9',
    mediumGray: '#e8ede9',
    textDark: '#1a1a1a',
    textLight: '#6b7280',
    white: '#ffffff',
    success: '#4a7c2a',
    warning: '#f59e0b',
    danger: '#ef4444'
  };

  const isMobile = windowWidth <= 768;
  const isTablet = windowWidth > 768 && windowWidth <= 1024;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchDashboardData();
    // Auto-refresh every 15 seconds to get latest order statuses and sales data
    const interval = setInterval(fetchDashboardData, 15000);
    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const data = await apiGet('/admin/pos/dashboard/stats');
      if (data.success) {
        setDashboardData(data.data);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
    setLoading(false);
  };

  const handleGenerateInvoice = async (orderId, orderNumber) => {
    if (!window.confirm(`Generate invoice for order ${orderNumber}?\n\nThis will:\n- Mark order as completed\n- Free the table (if dine-in)\n- Generate downloadable bill`)) {
      return;
    }

    try {
      const response = await fetch(`${PRODUCTION_URL}/api/admin/pos/orders/${orderId}/generate-invoice`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();
      if (data.success) {
        // Download the bill
        window.open(`${PRODUCTION_URL}/api/admin/pos/bills/${orderId}/download`, '_blank');

        // Refresh dashboard
        setTimeout(() => fetchDashboardData(), 1000);

        alert(`✅ Invoice generated for ${orderNumber}!`);
      }
    } catch (error) {
      console.error('Error generating invoice:', error);
      alert('Error generating invoice');
    }
  };

  const downloadBill = (orderId) => {
    window.open(`${PRODUCTION_URL}/api/admin/pos/bills/${orderId}/download`, '_blank');
  };

  const getStatusColor = (status) => {
    const statusColors = {
      pending: colors.warning,
      preparing: colors.accent,
      ready: colors.success,
      completed: colors.textLight,
      cancelled: colors.danger,
      served: colors.primary
    };
    return statusColors[status] || colors.textLight;
  };

  const styles = {
    pageHeader: {
      marginBottom: isMobile ? '16px' : '24px',
      padding: isMobile ? '0 4px' : '0'
    },
    pageTitle: {
      fontSize: isMobile ? '20px' : isTablet ? '24px' : '28px',
      fontWeight: '700',
      color: colors.textDark,
      marginBottom: '6px'
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
      gap: isMobile ? '12px' : '20px',
      marginBottom: isMobile ? '20px' : '24px',
      padding: isMobile ? '0 4px' : '0'
    },
    statCard: {
      background: colors.white,
      padding: isMobile ? '20px' : '24px',
      borderRadius: isMobile ? '14px' : '16px',
      boxShadow: '0 4px 12px rgba(45, 80, 22, 0.08)',
      border: `1px solid ${colors.mediumGray}`
    },
    statValue: {
      fontSize: isMobile ? '28px' : '36px',
      fontWeight: '800',
      marginBottom: '8px'
    },
    statLabel: {
      fontSize: isMobile ? '11px' : '12px',
      color: colors.textLight,
      textTransform: 'uppercase',
      fontWeight: '600'
    },
    contentCard: {
      background: colors.white,
      padding: isMobile ? '16px' : '24px',
      borderRadius: isMobile ? '14px' : '16px',
      boxShadow: '0 8px 24px rgba(45, 80, 22, 0.08)',
      border: `1px solid ${colors.mediumGray}`,
      margin: isMobile ? '0 4px 16px 4px' : '0 0 24px 0'
    },
    sectionTitle: {
      fontSize: isMobile ? '16px' : '20px',
      fontWeight: '700',
      color: colors.textDark,
      marginBottom: isMobile ? '12px' : '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: isMobile ? '12px' : '13px'
    },
    tableHeader: {
      background: colors.lightGray,
      fontWeight: '700',
      textAlign: 'left',
      padding: isMobile ? '10px' : '12px',
      borderBottom: `2px solid ${colors.mediumGray}`
    },
    tableCell: {
      padding: isMobile ? '10px' : '12px',
      borderBottom: `1px solid ${colors.lightGray}`
    },
    button: {
      padding: '6px 12px',
      borderRadius: '6px',
      border: 'none',
      fontSize: '11px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
            <div>Loading dashboard...</div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div style={styles.pageHeader}>
        <h1 style={styles.pageTitle}>POS Dashboard</h1>
        <p style={{ fontSize: isMobile ? '12px' : '14px', color: colors.textLight }}>
          {isMobile ? 'Sales overview' : 'Point of sale dashboard with sales analytics and performance metrics'}
        </p>
      </div>

      <div style={styles.statsGrid}>
        <div style={{ ...styles.statCard, borderTop: `4px solid ${colors.primary}` }}>
          <div style={{ ...styles.statValue, color: colors.primary }}>
            ₹{parseFloat(dashboardData.todaySales.total_revenue || 0).toLocaleString('en-IN')}
          </div>
          <div style={styles.statLabel}>Today's Revenue</div>
        </div>
        <div style={{ ...styles.statCard, borderTop: `4px solid ${colors.success}` }}>
          <div style={{ ...styles.statValue, color: colors.success }}>
            {dashboardData.todaySales.total_orders || 0}
          </div>
          <div style={styles.statLabel}>Orders Today</div>
        </div>
        <div style={{ ...styles.statCard, borderTop: `4px solid ${colors.accent}` }}>
          <div style={{ ...styles.statValue, color: colors.accent }}>
            ₹{parseFloat(dashboardData.todaySales.average_order_value || 0).toLocaleString('en-IN')}
          </div>
          <div style={styles.statLabel}>Avg Order Value</div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: isMobile ? '16px' : '20px' }}>
        <div style={styles.contentCard}>
          <h2 style={styles.sectionTitle}><span>🔥</span> Top Selling Items</h2>
          {dashboardData.topItems.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '30px', color: colors.textLight }}>
              No sales data for today
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {dashboardData.topItems.map((item, index) => (
                <div key={index} style={{
                  padding: isMobile ? '12px' : '14px',
                  background: colors.lightGray,
                  borderRadius: '8px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div>
                    <div style={{ fontSize: isMobile ? '13px' : '14px', fontWeight: '600', color: colors.textDark }}>
                      {item.item_name}
                    </div>
                    <div style={{ fontSize: '11px', color: colors.textLight }}>
                      Sold: {item.total_quantity}
                    </div>
                  </div>
                  <div style={{ fontSize: isMobile ? '14px' : '16px', fontWeight: '700', color: colors.primary }}>
                    ₹{parseFloat(item.total_revenue).toLocaleString('en-IN')}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={styles.contentCard}>
          <h2 style={styles.sectionTitle}><span>📊</span> Revenue by Category</h2>
          {dashboardData.categoryRevenue.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '30px', color: colors.textLight }}>
              No category data available
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {dashboardData.categoryRevenue.map((cat, index) => {
                const maxRevenue = Math.max(...dashboardData.categoryRevenue.map(c => parseFloat(c.revenue)));
                const percentage = (parseFloat(cat.revenue) / maxRevenue) * 100;

                return (
                  <div key={index}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '13px' }}>
                      <span style={{ fontWeight: '600' }}>{cat.category_name}</span>
                      <span style={{ color: colors.textLight }}>{cat.items_sold} items</span>
                    </div>
                    <div style={{
                      height: '32px',
                      background: colors.primary,
                      borderRadius: '4px',
                      width: `${percentage}%`,
                      minWidth: '80px',
                      display: 'flex',
                      alignItems: 'center',
                      paddingLeft: '10px',
                      color: colors.white,
                      fontSize: '12px',
                      fontWeight: '600'
                    }}>
                      ₹{parseFloat(cat.revenue).toLocaleString('en-IN')}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div style={styles.contentCard}>
        <h2 style={styles.sectionTitle}><span>📋</span> Recent Orders</h2>
        {dashboardData.recentOrders.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '30px', color: colors.textLight }}>
            No recent orders
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.tableHeader}>Order #</th>
                  {!isMobile && <th style={styles.tableHeader}>Table/Room</th>}
                  <th style={styles.tableHeader}>Type</th>
                  <th style={styles.tableHeader}>Amount</th>
                  <th style={styles.tableHeader}>Status</th>
                  {!isMobile && <th style={styles.tableHeader}>Time</th>}
                  <th style={styles.tableHeader}>Action</th>
                </tr>
              </thead>
              <tbody>
                {dashboardData.recentOrders.map(order => (
                  <tr key={order.id}>
                    <td style={styles.tableCell}><strong>{order.order_number}</strong></td>
                    {!isMobile && (
                      <td style={styles.tableCell}>
                        {order.table_number ? `🍽️ Table ${order.table_number}` :
                          order.room_number ? `🏨 Room ${order.room_number}` : '-'}
                      </td>
                    )}
                    <td style={styles.tableCell}>
                      <span style={{ textTransform: 'capitalize' }}>
                        {order.order_type.replace('_', ' ')}
                      </span>
                    </td>
                    <td style={styles.tableCell}>
                      <strong>₹{parseFloat(order.final_amount).toLocaleString('en-IN')}</strong>
                    </td>
                    <td style={styles.tableCell}>
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '6px',
                        fontSize: '11px',
                        fontWeight: '600',
                        background: `${getStatusColor(order.order_status)}20`,
                        color: getStatusColor(order.order_status)
                      }}>
                        {order.order_status}
                      </span>
                    </td>
                    {!isMobile && (
                      <td style={styles.tableCell}>
                        {new Date(order.created_at).toLocaleTimeString('en-IN', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </td>
                    )}
                    <td style={styles.tableCell}>
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        {/* Show "Generate Invoice" for ready/served dine-in orders */}
                        {(order.order_status === 'ready' || order.order_status === 'served') &&
                          order.order_type === 'dine_in' && (
                            <button
                              onClick={() => handleGenerateInvoice(order.id, order.order_number)}
                              style={{
                                ...styles.button,
                                background: colors.primary,
                                color: colors.white
                              }}
                            >
                              📄 Invoice
                            </button>
                          )}

                        {/* Show "View Bill" for completed/paid orders */}
                        {(order.order_status === 'completed' || order.payment_status === 'paid') && (
                          <button
                            onClick={() => downloadBill(order.id)}
                            style={{
                              ...styles.button,
                              background: colors.accent,
                              color: colors.white
                            }}
                          >
                            📥 Bill
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default PosDashboard;